import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ChevronDown, ChevronRight, Download, Link2, Save, Send, Trash2, Undo2,
} from "lucide-react";
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";

import {
  asignarNominacionCarga,
  descargarNominacion,
  descartarNominacion,
  getNominacionCargas,
  getNominacionDetalle,
  getNominacionResumen,
  guardarNominacion,
  type NomDetalle,
  type NomDia,
  type NomHora,
  type NomPayload,
} from "@/lib/api";

const num = (n: number | null | undefined, dec = 3) =>
  n == null || Number.isNaN(n) ? "—" : n.toLocaleString("es-MX", {
    minimumFractionDigits: dec, maximumFractionDigits: dec,
  });

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

const fechaHora = (iso: string) =>
  new Date(iso).toLocaleString("es-MX", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

const claveDia = (d: { fecha: string; carga: number }) => `${d.fecha}|${d.carga}`;

/** Hoy ± n días en la zona del navegador, como AAAA-MM-DD. */
function hoyMas(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** % redondeado a 2 decimales; null si el pronóstico es 0. */
const pctDe = (arb: number, pron: number) =>
  pron ? Math.round((arb / pron) * 100 * 100) / 100 : null;

const aNum = (s: string) => {
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

// Lo que el usuario está tecleando en cada hora (texto, para no pelear con el input).
type Edicion = Record<number, { arb: string; pct: string }>;
type Filtro = "propuestas" | "guardadas" | "todas";

const detalleKey = (d: { fecha: string; carga: number }) =>
  ["nominacion-detalle", d.fecha, d.carga] as const;

/** Horas de partida de un día: lo guardado si existe, si no la propuesta. */
const horasBase = (det: NomDetalle): NomHora[] => det.guardada ?? det.propuesta;

/** Horas finales (pronóstico + arbitraje editado) a partir de la base y la edición. */
function horasFinales(base: NomHora[], ed: Edicion | undefined): NomHora[] {
  return base.map((h) => {
    const e = ed?.[h.hora];
    const arb = e ? aNum(e.arb) : h.arbitraje_mw;
    return {
      hora: h.hora,
      pronostico_mw: h.pronostico_mw,
      arbitraje_mw: arb,
      pct_arbitraje: e ? (e.pct.trim() === "" ? null : aNum(e.pct)) : h.pct_arbitraje,
      nominacion_mw: h.pronostico_mw + arb,
    };
  });
}

/*
 * Operación → Contrapartes → Nominación a contraparte.
 *
 * Por día y "Carga N" del pronóstico de Luxem se propone la nominación horaria:
 *   Nominación = Pronóstico (Pronósticos Luxem) + Arbitraje (Demanda Fija de Arbitraje)
 * con % arbitraje = Arbitraje ÷ Pronóstico (2 decimales). Solo Arbitraje y % se
 * editan (uno recalcula al otro). Por default se ven las PROPUESTAS (días sin
 * guardar). Cada día se guarda (etrm.nominacion, mig 116) y SOLO lo guardado se
 * descarga (regla del usuario 13-oct-2026), en la
 * plantilla de CENACE "Oferta de Compra de Energía" (OCEvr1) con la nominación
 * como Demanda Fija.
 */
export function NominacionPage() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>("propuestas");
  const [abierto, setAbierto] = useState<string | null>(null);
  const [ediciones, setEdiciones] = useState<Record<string, Edicion>>({});
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [verCargas, setVerCargas] = useState(false);
  // Rango de fechas de operación: por default hoy − 3 … hoy + 3 (pedido del usuario).
  const [desde, setDesde] = useState(() => hoyMas(-3));
  const [hasta, setHasta] = useState(() => hoyMas(3));
  const [contraparte, setContraparte] = useState<number | "">("");

  const q = useQuery({
    queryKey: ["nominacion-resumen", desde, hasta],
    queryFn: () => getNominacionResumen({ desde, hasta }),
  });
  const enRango = q.data?.items ?? [];
  const contrapartes = [...new Map(enRango
    .filter((d) => d.id_contraparte != null)
    .map((d) => [d.id_contraparte!, d.alias || d.contraparte || `#${d.id_contraparte}`])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const items = contraparte === "" ? enRango : enRango.filter((d) => d.id_contraparte === contraparte);
  const cuenta = {
    propuestas: items.filter((d) => !d.guardada).length,
    guardadas: items.filter((d) => d.guardada).length,
    todas: items.length,
  };
  const visibles = items.filter((d) =>
    filtro === "todas" ? true : filtro === "guardadas" ? d.guardada : !d.guardada);
  const sinLiga = [...new Set(items.filter((d) => d.id_contraparte == null).map((d) => d.carga))];

  const setEdicion = (k: string, ed: Edicion | null) =>
    setEdiciones((prev) => {
      const sig = { ...prev };
      if (ed) sig[k] = ed; else delete sig[k];
      return sig;
    });

  /** Arma lo que se manda al backend con lo que haya en pantalla. */
  async function payloadDe(d: NomDia): Promise<NomPayload> {
    const det = await qc.fetchQuery({
      queryKey: detalleKey(d), queryFn: () => getNominacionDetalle(d),
    });
    const horas = horasFinales(horasBase(det), ediciones[claveDia(d)]);
    const mala = horas.find((h) => Number.isNaN(h.arbitraje_mw));
    if (mala) throw new Error(`Hora ${mala.hora}: el arbitraje no es un número.`);
    return {
      fecha: d.fecha, carga: d.carga, sistema: det.sistema,
      horas: horas.map((h) => ({ hora: h.hora, arbitraje_mw: h.arbitraje_mw })),
    };
  }

  async function accion(d: NomDia, tipo: "guardar" | "descargar" | "descartar") {
    const k = claveDia(d);
    setError(null);
    setAviso(null);
    if (tipo === "descartar" && !window.confirm(
      `¿Descartar la nominación guardada de ${d.centro_carga} del ${fechaCorta(d.fecha)}? `
      + "El día vuelve a ser propuesta.")) return;
    setOcupado(`${k}|${tipo}`);
    try {
      if (tipo === "descartar") {
        await descartarNominacion(d);
        setEdicion(k, null);
        setAviso(`Nominación de ${d.centro_carga} del ${fechaCorta(d.fecha)} descartada.`);
      } else if (tipo === "guardar") {
        const r = await guardarNominacion(await payloadDe(d));
        setEdicion(k, null);
        setAviso(`Nominación de ${d.centro_carga} del ${fechaCorta(d.fecha)} guardada `
          + `(${num(r.nominacion_mwh)} MWh).`);
      } else {
        await descargarNominacion(d);   // solo guardadas: sale de la BD
      }
      if (tipo !== "descargar") {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["nominacion-resumen"] }),
          qc.invalidateQueries({ queryKey: detalleKey(d) }),
        ]);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-slate-500" /> Nominación a contraparte
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Nominación = pronóstico de{" "}
            <Link to="/pronosticos-luxem" className="text-blue-600 hover:underline">Pronósticos Luxem</Link>{" "}
            + <Link to="/arbitraje" className="text-blue-600 hover:underline">Arbitraje</Link>.
            Solo se editan <b>Arbitraje</b> y <b>% arbitraje</b>; cada día se guarda y, ya guardado, se
            descarga en la plantilla de CENACE <i>Oferta de Compra de Energía</i> (OCEvr1).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setVerCargas((v) => !v)}
          className="text-sm inline-flex items-center gap-1 px-3 py-1.5 border rounded bg-white hover:bg-slate-50 text-slate-700"
        >
          <Link2 className="w-4 h-4" /> Cargas Luxem ↔ centro de carga
        </button>
      </div>

      {(verCargas || sinLiga.length > 0) && (
        <PanelCargas sinLiga={sinLiga} onCambio={() => {
          qc.invalidateQueries({ queryKey: ["nominacion-resumen"] });
          qc.invalidateQueries({ queryKey: ["nominacion-detalle"] });
        }} />
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {aviso}
        </div>
      )}

      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Contraparte</span>
          <select
            value={contraparte}
            onChange={(e) => setContraparte(e.target.value === "" ? "" : Number(e.target.value))}
            className="border rounded px-2 py-1 bg-white min-w-48"
          >
            <option value="">Todas</option>
            {contrapartes.map(([id, nombre]) => (
              <option key={id} value={id}>{nombre}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Pronóstico desde</span>
          <input type="date" value={desde} max={hasta || undefined}
            onChange={(e) => setDesde(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">hasta</span>
          <input type="date" value={hasta} min={desde || undefined}
            onChange={(e) => setHasta(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <button
          type="button"
          onClick={() => { setDesde(hoyMas(-3)); setHasta(hoyMas(3)); setContraparte(""); }}
          className="px-3 py-1 border rounded bg-white hover:bg-slate-50 text-slate-600"
          title="Volver a hoy − 3 … hoy + 3 y todas las contrapartes"
        >
          Restablecer
        </button>
      </div>

      <div className="flex items-center gap-1 text-sm">
        {(["propuestas", "guardadas", "todas"] as Filtro[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFiltro(f)}
            className={`px-3 py-1.5 rounded border ${filtro === f
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            {f === "propuestas" ? "Propuestas" : f === "guardadas" ? "Guardadas" : "Todas"}
            <span className="ml-1.5 text-xs opacity-70">{cuenta[f]}</span>
          </button>
        ))}
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {q.isSuccess && visibles.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          {filtro === "propuestas"
            ? "No hay propuestas pendientes en el rango y contraparte elegidos."
            : filtro === "guardadas" ? "No hay nominaciones guardadas en el rango y contraparte elegidos."
              : "No hay pronósticos de Luxem en el rango y contraparte elegidos."}
        </div>
      )}

      {q.isSuccess && visibles.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-2 py-2 w-6" />
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Carga</th>
                <th className="px-3 py-2">Contraparte</th>
                <th className="px-3 py-2">Centro de carga</th>
                <th className="px-3 py-2">Sistema</th>
                <th className="px-3 py-2 text-right">Pronóstico MWh</th>
                <th className="px-3 py-2 text-right">Arbitraje MWh</th>
                <th className="px-3 py-2 text-right">% arbitraje</th>
                <th className="px-3 py-2 text-right">Nominación MWh</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((d) => {
                const k = claveDia(d);
                const abiertoEste = abierto === k;
                const ligada = d.id_contraparte != null;
                const editado = !!ediciones[k];
                const busy = (t: string) => ocupado === `${k}|${t}`;
                return (
                  <Fragment key={k}>
                    <tr
                      className={`border-t ${ligada ? "cursor-pointer hover:bg-slate-50" : "text-slate-400"} ${abiertoEste ? "bg-slate-50" : ""}`}
                      onClick={() => ligada && setAbierto(abiertoEste ? null : k)}
                    >
                      <td className="px-2 py-1.5 text-slate-400">
                        {ligada && (abiertoEste
                          ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fechaCorta(d.fecha)}</td>
                      <td className="px-3 py-1.5">Carga {d.carga}</td>
                      <td className="px-3 py-1.5">{d.alias || d.contraparte || "—"}</td>
                      <td className="px-3 py-1.5">
                        {d.centro_carga ?? <span className="text-amber-700">sin asignar</span>}
                      </td>
                      <td className="px-3 py-1.5">{d.sistema}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(d.pronostico_mwh)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {d.sin_arbitraje && !d.guardada
                          ? <span className="text-amber-700" title="No hay arbitraje cargado para este día y centro de carga">sin arbitraje</span>
                          : num(d.arbitraje_mwh)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {d.pct_arbitraje == null ? "—" : `${num(d.pct_arbitraje, 2)} %`}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{num(d.nominacion_mwh)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <Estado d={d} editado={editado} />
                      </td>
                      <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            disabled={!ligada || !!ocupado}
                            onClick={() => accion(d, "guardar")}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                            title="Guardar la nominación de este día"
                          >
                            <Save className="w-3.5 h-3.5" /> {busy("guardar") ? "…" : "Guardar"}
                          </button>
                          <button
                            type="button"
                            disabled={!ligada || !d.guardada || editado || !!ocupado}
                            onClick={() => accion(d, "descargar")}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                            title={!d.guardada
                              ? "Guarda la nominación para poder descargarla"
                              : editado
                                ? "Hay cambios sin guardar: guárdalos (o deshazlos) para descargar"
                                : "Descargar el archivo de nominación guardado (plantilla CENACE OCEvr1)"}
                          >
                            <Download className="w-3.5 h-3.5" /> {busy("descargar") ? "…" : "Descargar"}
                          </button>
                          {d.guardada && (
                            <button
                              type="button"
                              disabled={!!ocupado}
                              onClick={() => accion(d, "descartar")}
                              className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                              title="Descartar lo guardado (el día vuelve a ser propuesta)"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {abiertoEste && (
                      <tr className="border-t bg-slate-50/60">
                        <td />
                        <td colSpan={11} className="px-3 py-3">
                          <DetalleDia
                            dia={d}
                            edicion={ediciones[k]}
                            onEdicion={(ed) => setEdicion(k, ed)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Estado({ d, editado }: { d: NomDia; editado: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      {d.guardada ? (
        <span
          className="inline-flex w-fit items-center rounded px-1.5 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
          title={d.guardado_por ?? undefined}
        >
          Guardada {d.guardado_en ? fechaHora(d.guardado_en) : ""}
        </span>
      ) : (
        <span className="inline-flex w-fit items-center rounded px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 border border-slate-200">
          Propuesta
        </span>
      )}
      {editado && <span className="text-xs text-blue-700">con cambios sin guardar</span>}
      {d.pronostico_cambio && (
        <span className="text-xs text-amber-700 inline-flex items-center gap-1"
          title="El pronóstico de Luxem se volvió a cargar después de guardar; descarta y vuelve a guardar para tomarlo">
          <AlertTriangle className="w-3 h-3" /> pronóstico cambió
        </span>
      )}
    </div>
  );
}

function DetalleDia({ dia, edicion, onEdicion }: {
  dia: NomDia;
  edicion: Edicion | undefined;
  onEdicion: (ed: Edicion | null) => void;
}) {
  const q = useQuery({ queryKey: detalleKey(dia), queryFn: () => getNominacionDetalle(dia) });
  const [pctTodas, setPctTodas] = useState("");

  if (q.isLoading) return <div className="text-sm text-slate-500">Cargando horas…</div>;
  if (q.isError) return <div className="text-sm text-red-600">{(q.error as Error).message}</div>;
  const det = q.data!;
  const base = horasBase(det);
  const horas = horasFinales(base, edicion);

  /** Edición completa (todas las horas) partiendo de lo que hay. */
  const edicionActual = (): Edicion => Object.fromEntries(base.map((h) => [h.hora, edicion?.[h.hora] ?? {
    arb: String(Math.round(h.arbitraje_mw * 10000) / 10000),
    pct: h.pct_arbitraje == null ? "" : h.pct_arbitraje.toFixed(2),
  }]));

  const cambiarArb = (h: NomHora, txt: string) => {
    const arb = aNum(txt);
    const pct = Number.isNaN(arb) ? null : pctDe(arb, h.pronostico_mw);
    onEdicion({ ...edicionActual(), [h.hora]: { arb: txt, pct: pct == null ? "" : pct.toFixed(2) } });
  };
  const cambiarPct = (h: NomHora, txt: string) => {
    const pct = aNum(txt);
    const arb = Number.isNaN(pct) ? NaN : Math.round(h.pronostico_mw * pct / 100 * 10000) / 10000;
    onEdicion({
      ...edicionActual(),
      [h.hora]: { pct: txt, arb: Number.isNaN(arb) ? (edicion?.[h.hora]?.arb ?? "") : String(arb) },
    });
  };
  const aplicarTodas = () => {
    const pct = aNum(pctTodas);
    if (Number.isNaN(pct) || pctTodas.trim() === "") return;
    onEdicion(Object.fromEntries(base.map((h) => [h.hora, {
      pct: pct.toFixed(2),
      arb: String(Math.round(h.pronostico_mw * pct / 100 * 10000) / 10000),
    }])));
  };

  const tot = horas.reduce((a, h) => ({
    pron: a.pron + h.pronostico_mw,
    arb: a.arb + (Number.isNaN(h.arbitraje_mw) ? 0 : h.arbitraje_mw),
    nom: a.nom + (Number.isNaN(h.nominacion_mw) ? 0 : h.nominacion_mw),
  }), { pron: 0, arb: 0, nom: 0 });

  const input = "w-24 text-right tabular-nums border rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span>
          {det.contraparte} · <b>{det.centro_carga}</b> · {det.sistema} · Carga {det.carga} de Luxem
        </span>
        {det.guardada
          ? <span>Mostrando lo <b>guardado</b>{det.guardado_por ? ` por ${det.guardado_por}` : ""}.</span>
          : <span>Mostrando la <b>propuesta</b> (pronóstico + arbitraje cargados).</span>}
        <span className="ml-auto inline-flex items-center gap-1">
          % a todas las horas:
          <input
            value={pctTodas}
            onChange={(e) => setPctTodas(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aplicarTodas()}
            className={`${input} w-20`}
            placeholder="35.00"
          />
          <button type="button" onClick={aplicarTodas}
            className="px-2 py-0.5 border rounded bg-white hover:bg-slate-50">Aplicar</button>
          {edicion && (
            <button type="button" onClick={() => onEdicion(null)}
              className="px-2 py-0.5 border rounded bg-white hover:bg-slate-50 inline-flex items-center gap-1"
              title="Quitar los cambios sin guardar">
              <Undo2 className="w-3 h-3" /> Deshacer cambios
            </button>
          )}
        </span>
      </div>
      <table className="text-sm bg-white border rounded">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-3 py-1.5 text-right">Hora</th>
            <th className="px-3 py-1.5 text-right">Pronóstico MW</th>
            <th className="px-3 py-1.5 text-right">Arbitraje MW</th>
            <th className="px-3 py-1.5 text-right">% arbitraje</th>
            <th className="px-3 py-1.5 text-right">Nominación MW</th>
          </tr>
        </thead>
        <tbody>
          {horas.map((h) => {
            const e = edicion?.[h.hora];
            return (
              <tr key={h.hora} className="border-t">
                <td className="px-3 py-0.5 text-right tabular-nums text-slate-500">{h.hora}</td>
                <td className="px-3 py-0.5 text-right tabular-nums">{num(h.pronostico_mw, 4)}</td>
                <td className="px-3 py-0.5 text-right">
                  <input
                    className={`${input} ${Number.isNaN(h.arbitraje_mw) ? "border-red-400" : ""}`}
                    value={e ? e.arb : String(Math.round(h.arbitraje_mw * 10000) / 10000)}
                    onChange={(ev) => cambiarArb(h, ev.target.value)}
                  />
                </td>
                <td className="px-3 py-0.5 text-right">
                  <input
                    className={input}
                    disabled={!h.pronostico_mw}
                    title={h.pronostico_mw ? undefined : "Sin pronóstico en esta hora"}
                    value={e ? e.pct : (h.pct_arbitraje == null ? "" : h.pct_arbitraje.toFixed(2))}
                    onChange={(ev) => cambiarPct(h, ev.target.value)}
                  />
                </td>
                <td className="px-3 py-0.5 text-right tabular-nums font-medium">{num(h.nominacion_mw, 4)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t bg-slate-50 font-medium">
          <tr>
            <td className="px-3 py-1.5 text-right text-slate-500">Total</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{num(tot.pron)}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{num(tot.arb)}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">
              {tot.pron ? `${num(pctDe(tot.arb, tot.pron), 2)} %` : "—"}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums">{num(tot.nom)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PanelCargas({ sinLiga, onCambio }: { sinLiga: number[]; onCambio: () => void }) {
  const q = useQuery({ queryKey: ["nominacion-cargas"], queryFn: getNominacionCargas });
  const qc = useQueryClient();
  const asignar = useMutation({
    mutationFn: asignarNominacionCarga,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nominacion-cargas"] });
      onCambio();
    },
  });
  const opciones = q.data?.opciones ?? [];
  const valor = (o: { id_contraparte: number | null; centro_carga: string | null }) =>
    o.id_contraparte == null ? "" : `${o.id_contraparte}|${o.centro_carga}`;

  return (
    <div className="bg-white border rounded-lg p-4 space-y-2">
      <div className="text-sm font-medium text-slate-800">Cargas de Luxem ↔ centro de carga</div>
      <p className="text-xs text-slate-500">
        El Excel de Luxem trae "Carga 1, Carga 2…" sin decir a qué centro de carga corresponden. Aquí se
        liga cada carga con el centro de carga de Arbitraje del que se toma el arbitraje.
      </p>
      {sinLiga.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 text-xs">
          Sin centro de carga: {sinLiga.map((c) => `Carga ${c}`).join(", ")}. Esos días no se pueden
          guardar ni descargar hasta asignarlo.
        </div>
      )}
      {asignar.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2 text-xs">
          {(asignar.error as Error).message}
        </div>
      )}
      {q.isLoading && <div className="text-xs text-slate-500">Cargando…</div>}
      <div className="flex flex-wrap gap-3">
        {(q.data?.items ?? []).map((c) => (
          <label key={c.carga} className="text-sm flex items-center gap-2 border rounded px-2 py-1">
            <span className="text-slate-600">Carga {c.carga}</span>
            <select
              value={valor(c)}
              disabled={asignar.isPending}
              onChange={(e) => {
                const [id, ...cc] = e.target.value.split("|");
                if (id) asignar.mutate({ carga: c.carga, id_contraparte: Number(id), centro_carga: cc.join("|") });
              }}
              className="border rounded px-1.5 py-0.5 text-sm bg-white"
            >
              <option value="">— sin asignar —</option>
              {opciones.map((o) => (
                <option key={valor(o)} value={valor(o)}>{o.contraparte} · {o.centro_carga}</option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
}
